import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import FileExport from '../plugins/fileExport';
import { isNativePlatform, isWeb } from './platform';
import { downloadCSV } from './webFileExport';

interface SaveCsvOptions {
  filename: string;
  csvData: string;
  mimeType?: string;
}

/**
 * Save a CSV to the device and share it
 * Works on both iOS and Android
 */
export const saveCsvToDevice = async (options: SaveCsvOptions): Promise<{ success: boolean; path?: string; error?: string }> => {
  const { filename, csvData } = options;
  const mimeType = options.mimeType || 'text/csv';
  
  
  
  try {
    // If we're on a native platform
    if (isNativePlatform()) {
      try {
        // Convert to base64
        const base64Data = btoa(unescape(encodeURIComponent(csvData)));
        
        // Try our custom plugin which has better file handling for iOS
        if (Capacitor.getPlatform() === 'ios') {
          try {
            
            const result = await FileExport.saveFile({
              filename,
              data: base64Data,
              mimeType
            });
            
            
            return { 
              success: true, 
              path: result.path
            };
          } catch (pluginError) {
            
            // Fall through to standard approach
          }
        }
        
        // Standard Capacitor approach (works on Android and iOS)
        
        try {
          const savedFile = await Filesystem.writeFile({
            path: filename,
            data: base64Data,
            directory: Directory.Cache,
            recursive: true
          });
          
          
          
          // Share the file
          try {
            await Share.share({
              title: filename,
              text: 'CSV File',
              url: savedFile.uri,
              dialogTitle: 'Save or Share CSV'
            });
            
            return {
              success: true,
              path: savedFile.uri
            };
          } catch (shareError) {
            
            // Even if sharing fails, we still saved the file successfully
            return {
              success: true,
              path: savedFile.uri,
              error: 'File was saved but sharing failed'
            };
          }
        } catch (fileError) {
          
          throw new Error(`Failed to write file: ${fileError.message || 'Unknown error'}`);
        }
      } catch (nativeError) {
        
        throw new Error(`Native platform error: ${nativeError.message || 'Unknown error'}`);
      }
    } else {
      // For web, use our improved web file export utility
      try {
        
        const result = await downloadCSV(filename, csvData);
        return result;
      } catch (webError) {
        
        throw new Error(`Web download error: ${webError.message || 'Unknown error'}`);
      }
    }
  } catch (error) {
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error saving CSV'
    };
  }
};

/**
 * Convert object array to CSV string
 */
export const objectsToCsv = <T extends Record<string, any>>(data: T[], headers?: { key: keyof T, label: string }[]): string => {
  if (!data.length) return '';
  
  let csvContent = '';
  
  // If headers are not provided, use object keys as headers
  const useHeaders = headers || Object.keys(data[0]).map(key => ({ 
    key: key as keyof T, 
    label: key 
  }));
  
  // Add headers
  csvContent += useHeaders.map(h => `"${h.label}"`).join(',') + '\n';
  
  // Add rows
  data.forEach(item => {
    const row = useHeaders.map(header => {
      const value = item[header.key];
      // Properly quote and escape values
      if (value === null || value === undefined) return '""';
      if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
      return `"${value}"`;
    }).join(',');
    csvContent += row + '\n';
  });
  
  return csvContent;
}; 